import { useEffect, useState } from "react";
import { Loader2, Save, ExternalLink, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import TenderExaminer from "./TenderExaminer";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const inpCls = "w-full bg-navy-950 border border-gold-500/20 px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500";
const STATUS_STYLE = {
  not_started: "border-white/20 text-white/50", preparing: "border-sky-400/50 text-sky-300",
  submitted: "border-amber-400/50 text-amber-300", listed: "border-emerald-400/50 text-emerald-300",
  on_hold: "border-white/20 text-white/35",
};
const FIELD_LABELS = {
  entity_abn: "Entity / ABN", locations: "Locations", contact: "Contact",
  insurances: "Insurances (PI / PL / Cyber, insurer, expiry)",
  current_programs: "Current programs", referees: "Referees",
  essential_eight: "ACSC Essential Eight position", iso27001: "ISO 27001 position",
};

function PanelRow({ p, statuses, onSaved }) {
  const [form, setForm] = useState({ status: p.status, notes: p.notes || "", next_action: p.next_action || "" });
  const [busy, setBusy] = useState(false);
  const dirty = form.status !== p.status || form.notes !== (p.notes || "") || form.next_action !== (p.next_action || "");

  const save = async () => {
    setBusy(true);
    try { await api.patch(`/tender/panels/${p.id}`, form); toast.success(`${p.name} updated`); onSaved(); }
    catch { toast.error("Could not save panel"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-gold-500/15 bg-navy-900/40 p-4 space-y-3" data-testid={`tender-panel-${p.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-white text-sm font-medium flex items-center gap-2">
            {p.name}
            {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-gold-400/60 hover:text-gold-300"><ExternalLink className="h-3.5 w-3.5" /></a>}
          </div>
          <Mono className="text-[0.5rem] text-white/40">{p.jurisdiction} · {p.category}</Mono>
          <p className="text-white/45 text-xs mt-1">{p.pathway}</p>
        </div>
        <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          data-testid={`panel-status-${p.id}`}
          className={`bg-navy-950 border px-3 py-1.5 text-xs font-mono uppercase tracking-widest cursor-pointer ${STATUS_STYLE[form.status]}`}>
          {statuses.map((s) => <option key={s} value={s} className="bg-navy-900 text-white">{s.replace("_", " ")}</option>)}
        </select>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block"><Mono className="text-[0.45rem] text-white/40 block mb-1">Next action</Mono>
          <input value={form.next_action} onChange={(e) => setForm((f) => ({ ...f, next_action: e.target.value }))} data-testid={`panel-next-${p.id}`} className={inpCls} /></label>
        <label className="block"><Mono className="text-[0.45rem] text-white/40 block mb-1">Notes</Mono>
          <input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid={`panel-notes-${p.id}`} className={inpCls} /></label>
      </div>
      {dirty && (
        <button onClick={save} disabled={busy} data-testid={`panel-save-${p.id}`} className="inline-flex items-center gap-1.5 bg-gold-500 text-navy-950 font-semibold px-4 py-1.5 text-xs rounded-sm hover:bg-gold-400 disabled:opacity-60">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
        </button>
      )}
    </div>
  );
}

export default function TenderTab() {
  const [data, setData] = useState(null);
  const [cap, setCap] = useState(null);
  const [capBusy, setCapBusy] = useState(false);
  const [newPanel, setNewPanel] = useState("");

  const load = () => api.get("/tender/panels").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/tender/capability").then((r) => setCap(r.data.fields)).catch(() => {}); }, []);

  const saveCap = async () => {
    setCapBusy(true);
    try { await api.put("/tender/capability", { fields: cap }); toast.success("Capability statement updated — the rendered document now uses these details."); }
    catch { toast.error("Could not save"); }
    finally { setCapBusy(false); }
  };

  const addPanel = async (e) => {
    e.preventDefault();
    if (!newPanel.trim()) return;
    await api.post("/tender/panels", { name: newPanel.trim() });
    setNewPanel(""); load();
  };

  const openCap = () => window.open(`${API}/business/capability?token=${localStorage.getItem("mdau_token")}`, "_blank");

  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold-400" /></div>;

  return (
    <div className="space-y-8" data-testid="tender-tab">
      <TenderExaminer />
      <div>
        <Mono className="text-gold-400/80 text-[0.55rem] block mb-1">Panel registrations — the tender roadmap</Mono>
        <p className="text-white/45 text-xs mb-4">Track each procurement panel from first registration to listed. Start with buy.nsw Registered tier and LocalBuy — both accept the capability statement below.</p>
        <div className="space-y-3">
          {data.panels.map((p) => <PanelRow key={p.id} p={p} statuses={data.statuses} onSaved={load} />)}
        </div>
        <form onSubmit={addPanel} className="flex gap-2 mt-3">
          <input value={newPanel} onChange={(e) => setNewPanel(e.target.value)} placeholder="Add another panel / scheme…" data-testid="add-panel-input" className={inpCls + " max-w-sm"} />
          <button type="submit" data-testid="add-panel-btn" className="inline-flex items-center gap-1.5 border border-gold-500/40 text-gold-300 px-4 text-sm hover:bg-gold-500/10"><Plus className="h-3.5 w-3.5" /> Add</button>
        </form>
      </div>

      {cap && (
        <div className="border border-gold-500/20 bg-navy-900/40 p-5 space-y-4" data-testid="capability-editor">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <Mono className="text-gold-400/80 text-[0.55rem] block">Capability statement — editable details</Mono>
              <p className="text-white/45 text-xs mt-1">These fields render straight into the Capability Statement document. Fill in the [TO CONFIRM] items before lodging any panel application.</p>
            </div>
            <button onClick={openCap} data-testid="open-capability-btn" className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 px-4 py-2 text-sm hover:bg-gold-500/10">
              <FileText className="h-4 w-4" /> View document
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {Object.keys(FIELD_LABELS).map((k) => (
              <label key={k} className={["current_programs", "insurances"].includes(k) ? "md:col-span-2 block" : "block"}>
                <Mono className="text-[0.45rem] text-white/40 block mb-1">{FIELD_LABELS[k]}</Mono>
                {["current_programs", "referees"].includes(k) ? (
                  <textarea rows={2} value={cap[k]} onChange={(e) => setCap((c) => ({ ...c, [k]: e.target.value }))} data-testid={`cap-${k}`} className={inpCls + " resize-none"} />
                ) : (
                  <input value={cap[k]} onChange={(e) => setCap((c) => ({ ...c, [k]: e.target.value }))} data-testid={`cap-${k}`} className={inpCls} />
                )}
              </label>
            ))}
          </div>
          <button onClick={saveCap} disabled={capBusy} data-testid="save-capability-btn" className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 font-semibold px-5 py-2 text-sm rounded-sm hover:bg-gold-400 disabled:opacity-60">
            {capBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save capability details
          </button>
        </div>
      )}
    </div>
  );
}
